import toPairs from "lodash/toPairs";
import randomSample from "lodash/sample";

import { mapToObject } from "collection-utils";

import { assert, panic, defined, assertNever } from "../Support";
import {
    ComponentDescription,
    TableGlideType,
    TableColumn,
    ArrayItemType,
    TableRefGlideType,
    ScreenDescription,
    ArrayScreenDescription,
    classScreenName,
    arrayScreenName,
    makeTableRef,
    ScreenDescriptionKind,
    chatScreenName,
    editClassScreenName,
    TypeSchema,
    TabDescription,
    getTableName,
    getTableColumn,
    areTableNamesEqual,
    getTableRefTableName,
    addClassScreenName,
    sheetNameForTable,
    isAddOrEditOrFormScreenDescription,
    ChatScreenDescription,
    isSingleRelationType,
    findTable,
    freeScreenPrefix,
    ShoppingCartScreenDescription,
    shoppingCartScreenName,
    ClassOrArrayScreenDescription,
} from "./description";
import {
    AppDescription,
    NonUserAppFeatures,
    isolateNonUserAppFeatures,
    UserFeatures,
    defaultAppDescriptionText,
    defaultNonUserAppFeatures,
    SourceMetadata,
    AppAuthentication,
    makeSourceMetadataForAppDescription,
    AppKind,
} from "../components/SerializedApp";
import { getDefaultArrayScreenHandler } from "./array-screens";
import { makeScreenProperty, getScreenProperty } from "./components/property-utils";
import { ComponentConfiguratorContext, makeSimpleAppDescriptionContext } from "./components";
import { logError, logInfo } from "../debug-print";
import { getRandomIcon } from "../remote-icon-type";
import { TableName } from "../database-strings";
import {
    needFavoritesPivotsForType,
    makeArrayScreenFavoritesPivots,
    makeArrayScreenDescription,
    makeClassScreenDescription,
    componentDescriptionForColumn,
    makeAddOrEditScreenDescriptionForTable,
} from "./make-screen";
import { EminenceFlags } from "../Database/eminence";
import { BuilderAction, BuilderActionsForApp } from "../Database/builder-actions";
import { AppSettings } from "../firebase-function-types";
import { randomTheme } from "../BaseTheme";
import { UserProfileTableInfo } from "../components/types";
import { ArrayMap } from "../support/array-map";
import { definedMap } from "@glideapps/ts-necessities";

export const MAX_TABS = 4;

export const chatScreen: ChatScreenDescription = { kind: ScreenDescriptionKind.Chat };
export const shoppingCartScreen: ShoppingCartScreenDescription = { kind: ScreenDescriptionKind.ShoppingCart };

export function isChatTab(tab: TabDescription): boolean {
    return getScreenProperty(tab.screenName) === chatScreenName;
}

export function isShoppingCartTab(tab: TabDescription): boolean {
    return getScreenProperty(tab.screenName) === shoppingCartScreenName;
}

export function isTabSpecial(tab: TabDescription): boolean {
    return isChatTab(tab) || isShoppingCartTab(tab);
}

export function arrayScreenUpdatesForUpdatedClassScreen(
    appID: string,
    type: ArrayItemType,
    appDescription: AppDescription,
    builderActions: BuilderActionsForApp,
    schema: TypeSchema
): readonly [string, Partial<ArrayScreenDescription> | undefined][] {
    if (!isSingleRelationType(type)) return [];
    const tableName = getTableRefTableName(type);

    const results: [string, Partial<ArrayScreenDescription> | undefined][] = [];

    const { screenDescriptions } = appDescription;
    const needPivot = needFavoritesPivotsForType(
        type,
        makeSimpleAppDescriptionContext(appID, appDescription, builderActions, schema)
    );
    for (const [screenName, screenDesc] of toPairs(screenDescriptions)) {
        if (screenDesc.kind !== ScreenDescriptionKind.Array) continue;
        if (!isSingleRelationType(screenDesc.type)) continue;
        if (!areTableNamesEqual(getTableRefTableName(screenDesc.type), tableName)) continue;

        let havePivot: string | boolean;
        if (screenDesc.pivots === undefined || screenDesc.pivots[1] === undefined) {
            havePivot = false;
        } else {
            const p = screenDesc.pivots[1];
            if (p.title !== undefined) {
                havePivot = p.title;
            } else {
                havePivot = true;
            }
        }

        let updates: Partial<ArrayScreenDescription> | undefined;
        if (needPivot !== havePivot) {
            updates = { pivots: makeArrayScreenFavoritesPivots(needPivot) };
        }

        results.push([screenName, updates]);
    }

    return results;
}

export function copyToFreeScreen(screenName: string, screen: ClassOrArrayScreenDescription): ScreenDescription {
    assert(!isAddOrEditOrFormScreenDescription(screenName, screen));
    const newScreen = { ...screen, fetchesData: true };
    if (newScreen.kind === ScreenDescriptionKind.Class) {
        return { ...newScreen, title: undefined };
    } else {
        return newScreen;
    }
}

export function addOrRemoveSpecialTabsIfNecessary(
    tabs: readonly TabDescription[],
    isPage: boolean
): readonly TabDescription[] {
    if (isPage) {
        return tabs.filter(t => {
            const screenName = getScreenProperty(t.screenName);
            return screenName !== undefined && screenName !== chatScreenName && screenName !== shoppingCartScreenName;
        });
    } else {
        const newTabs = [...tabs];
        if (!tabs.some(isChatTab)) {
            newTabs.push({
                title: "Chat",
                icon: "message-circle",
                hidden: true,
                inFlyout: false,
                screenName: makeScreenProperty(chatScreenName),
            });
        }
        if (!tabs.some(isShoppingCartTab)) {
            newTabs.push({
                title: "Shopping Cart",
                icon: "09-07-shopping-bag-side",
                hidden: true,
                inFlyout: false,
                screenName: makeScreenProperty(shoppingCartScreenName),
            });
        }
        return newTabs;
    }
}

const defaultEmojis = ["👩‍💻", "👨‍💻", "🤳", "💡", "👍", "💫", "✌️", "😁", "🥳", "🐣", "🌈", "🦄", "🥑"];

export function makeRandomAppSettings(title: string, authentication: AppAuthentication | undefined): AppSettings {
    return {
        title,
        iconImage: { emoji: defined(randomSample(defaultEmojis)) },
        theme: randomTheme("none"),
        authentication,
    };
}

export class ScreenDescriptionBuilder implements ComponentConfiguratorContext {
    public builderActions: BuilderActionsForApp = new Map();

    private readonly _screens: Map<string, ScreenDescription> = new Map();
    private readonly _buildingDetailScreens: Set<TableGlideType> = new Set();
    private readonly _buildingScreens: string[] = [];

    constructor(
        public readonly appID: string,
        public readonly schema: TypeSchema,
        public readonly userFeatures: UserFeatures,
        public readonly eminenceFlags: EminenceFlags
    ) {}

    public get appDescription(): AppDescription | undefined {
        return undefined;
    }

    public get columnsHiddenForNotebooks(): ArrayMap<TableName, ReadonlySet<string>> | undefined {
        return undefined;
    }

    public getBuilderAction(): BuilderAction | undefined {
        return undefined;
    }

    public findTable(ref: TableRefGlideType | TableName | undefined): TableGlideType | undefined {
        if (ref === undefined) return undefined;
        const table = findTable(this.schema, ref);
        if (table === undefined) {
            logError("Table not found", getTableRefTableName(ref).name);
            return undefined;
        }
        return table;
    }

    public get userProfileTableInfo(): UserProfileTableInfo | undefined {
        return undefined;
    }

    // FIXME: This is from back when we rewrote app descriptions, and it had
    // to handle cyclic screen dependencies.  We should be able to just assert
    // that there's only one screen being built.
    private buildScreen(screenName: string, build: () => ScreenDescription): string;
    private buildScreen(screenName: string, build: () => ScreenDescription | undefined): string | undefined;
    private buildScreen(screenName: string, build: () => ScreenDescription | undefined): string | undefined {
        if (this._screens.has(screenName)) return screenName;
        if (this._buildingScreens.indexOf(screenName) >= 0) return screenName;

        this._buildingScreens.push(screenName);

        try {
            const desc = build();
            if (desc === undefined) return undefined;
            this._screens.set(screenName, desc);
            return screenName;
        } finally {
            assert(this._buildingScreens.pop() === screenName);
        }
    }

    private makeScreensForArray(itemType: ArrayItemType): string {
        const screenName = arrayScreenName(itemType);
        const result = this.buildScreen(screenName, () => {
            return makeArrayScreenDescription(getDefaultArrayScreenHandler(), itemType, this, false);
        });
        if (result === undefined) {
            return assertNever(result);
        }

        if (isSingleRelationType(itemType)) {
            const table = this.findTable(itemType);
            if (table !== undefined) {
                this.makeDetailScreensForTable(table);
            }
        }

        return screenName;
    }

    private makeAllScreensForArray(items: ArrayItemType, forceSingleton: boolean): string {
        // We have to generate both screens if they're potentially possible,
        // because the builder might switch between them.
        const screenNameForArray = this.makeScreensForArray(items);
        if (isSingleRelationType(items)) {
            const table = this.findTable(items);
            if (table !== undefined) {
                const screenNameForClass = this.makeDetailScreensForTable(table);
                if (table.numDataRows === 1 || forceSingleton) {
                    return screenNameForClass;
                }
            }
        }
        return screenNameForArray;
    }

    private makeScreensForColumn(column: TableColumn): string | undefined {
        if (column.hidden === true) return undefined;

        switch (column.type.kind) {
            case "array":
                return this.makeAllScreensForArray(column.type.items, false);
            case "table-ref":
                const table = this.findTable(column.type);
                if (table === undefined) return undefined;
                return this.makeDetailScreensForTable(table);
            default:
                return undefined;
        }
    }

    private makeDetailScreensForTable(table: TableGlideType): string {
        const tableRef = makeTableRef(table);

        const screenName = classScreenName(tableRef);

        if (this._buildingDetailScreens.has(table)) {
            return screenName;
        }
        this._buildingDetailScreens.add(table);

        this.buildScreen(screenName, () => {
            return makeClassScreenDescription(table, this, false);
        });

        const editScreenName = editClassScreenName(tableRef);
        this.buildScreen(editScreenName, () => {
            return makeAddOrEditScreenDescriptionForTable(table, true);
        });

        const addScreenName = addClassScreenName(tableRef);
        this.buildScreen(addScreenName, () => {
            return makeAddOrEditScreenDescriptionForTable(table, false);
        });

        for (const column of table.columns) {
            this.makeScreensForColumn(column);
        }

        return screenName;
    }

    private makeTabDescription(title: string, screenName: string, index: number, hidden: boolean): TabDescription {
        const inFlyout = !hidden && index >= MAX_TABS;

        return {
            title,
            icon: getRandomIcon(this.eminenceFlags.proIcons),
            screenName: makeScreenProperty(screenName),
            hidden,
            inFlyout,
        };
    }

    // FIXME: It's really stupid that we take all these arguments.  There should
    // be another way to just get the updates to an existing app description without
    // passing all the other stuff in.
    public makeAppDescription(
        sourceMetadataArray: readonly SourceMetadata[],
        settings: AppSettings,
        features: NonUserAppFeatures | undefined
    ): AppDescription {
        const { schema } = this;

        const tabs: TabDescription[] = [];
        const tablesDone = new Set<TableGlideType>();

        for (const table of schema.tables) {
            const screenName = this.makeAllScreensForArray(makeTableRef(table), false);

            if (tablesDone.has(table)) continue;
            if (getTableName(table).isSpecial) continue;

            const screenCopyName = makeScreenNameWithPrefix(freeScreenPrefix, n => this._screens.has(n));
            const screenCopy = copyToFreeScreen(
                screenName,
                defined(this._screens.get(screenName)) as ClassOrArrayScreenDescription
            );
            this._screens.set(screenCopyName, screenCopy);

            tabs.push(
                this.makeTabDescription(
                    sheetNameForTable(table),
                    screenCopyName,
                    tabs.length,
                    // We hide tabs from new sheets in existing apps
                    false
                )
            );
        }

        const screenDescriptions = mapToObject(this._screens);

        // We have to generate this, in case the builder switches it on
        screenDescriptions[chatScreenName] = chatScreen;
        screenDescriptions[shoppingCartScreenName] = shoppingCartScreen;

        return {
            ...makeSourceMetadataForAppDescription(sourceMetadataArray),
            title: settings.title,
            author: settings.author,
            description: settings.description ?? defaultAppDescriptionText,
            iconImage: settings.iconImage,
            tabs: addOrRemoveSpecialTabsIfNecessary(tabs, features?.appKind === AppKind.Page),
            screenDescriptions,
            theme: settings.theme,
            authentication: settings.authentication,
            features: definedMap(features, isolateNonUserAppFeatures) ?? defaultNonUserAppFeatures,
            userProfile: undefined,
        };
    }
}

interface NewComponent {
    readonly table: TableGlideType;
    readonly column: TableColumn;
    readonly desc: ComponentDescription;
}

export function addNewComponents(appDesc: AppDescription, newComponents: ReadonlyArray<NewComponent>): AppDescription {
    const updatedScreenDescriptions: { [name: string]: ScreenDescription } = {};

    for (const { table, desc } of newComponents) {
        const screenName = classScreenName(getTableName(table));
        const screenDesc = appDesc.screenDescriptions[screenName];

        if (screenDesc.kind !== ScreenDescriptionKind.Class) {
            return panic(`Screen is not a class screen ${screenName} but a ${screenDesc.kind}`);
        }

        const newScreenDesc = { ...screenDesc, components: [...screenDesc.components, desc] };
        updatedScreenDescriptions[screenName] = newScreenDesc;
    }

    return { ...appDesc, screenDescriptions: { ...appDesc.screenDescriptions, ...updatedScreenDescriptions } };
}

export function newComponentsForNewColumns(
    appDesc: AppDescription,
    oldCCC: ComponentConfiguratorContext,
    newSchema: TypeSchema
): ReadonlyArray<NewComponent> {
    const newComponents: NewComponent[] = [];

    const oldSchema = oldCCC.schema;
    for (const table of oldSchema.tables) {
        const newTable = findTable(newSchema, getTableName(table));
        if (newTable === undefined) continue;

        const screenName = classScreenName(getTableName(newTable));
        const screenDesc = appDesc.screenDescriptions[screenName];
        if (screenDesc === undefined) continue;
        if (screenDesc.kind !== ScreenDescriptionKind.Class) {
            return panic(`Screen is not a class screen ${screenName} but a ${screenDesc.kind}`);
        }

        for (const column of newTable.columns) {
            const oldColumn = getTableColumn(table, column.name);
            if (oldColumn !== undefined) continue;

            logInfo("Column not found in old schema", column.name, table, newTable);

            const desc = componentDescriptionForColumn(oldCCC, column);
            if (desc === undefined) continue;

            newComponents.push({ table: newTable, column, desc });
        }
    }

    if (newComponents.length > 0) {
        logInfo("New components", newComponents, oldSchema, newSchema);
    }

    return newComponents;
}

export function makeScreenNameWithPrefix(prefix: string, nameExists: (n: string) => boolean): string {
    function makeName(suffix: string) {
        return prefix + suffix;
    }

    for (let i = 0; ; i++) {
        const n = makeName(i.toString());
        if (!nameExists(n)) {
            return n;
        }
    }
}
